'use client';

import { useEffect, useRef } from 'react';

// Vertical parallax drift for the halo image, based on its distance from the
// viewport center. No-op when reduced motion is requested.
export default function useHaloParallax<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;

    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        const element = ref.current;
        if (!element) return;

        const rect = element.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        const center = rect.top + rect.height / 2;
        const offset = Math.max(
          -70,
          Math.min(70, (window.innerHeight / 2 - center) * 0.08)
        );

        element.style.transform = `translateY(${offset.toFixed(1)}px)`;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}
